import {
  NewButton,
  NewButtonSize,
  NewButtonVariant,
  TooltipTrigger,
} from "@visiblethread/react-components";
import { Quote, Trash2 } from "lucide-react";
import { func, object, string } from "prop-types";
import { useEffect, useRef, useState } from "react";
import isNullish from "utils/isNullish";

import styles from "./RequirementsReviewGate.module.scss";
import VerificationBadge from "./VerificationBadge";

const requirementPropType = object;

export const buildDocumentLabelsById = (documents = []) => {
  const labels = {};

  documents.forEach((doc) => {
    if (isNullish(doc?.nextgenDocumentId)) return;

    labels[doc.nextgenDocumentId] =
      doc?.docTypeLabel || `Document ${doc.nextgenDocumentId}`;
  });

  return labels;
};

// An editable requirement text cell: local state so keystrokes don't round-trip
// through Redux, committed onBlur (only if the trimmed value actually changed and
// isn't blank — restatedText is @NotBlank server-side). Always threads the row's
// current category through so a text-only edit doesn't blank out the category.
export const RestatedTextCell = ({
  requirement,
  onUpdate,
  isRemoved = () => false,
}) => {
  const [value, setValue] = useState(requirement.restatedText || "");

  useEffect(() => {
    setValue(requirement.restatedText || "");
  }, [requirement.restatedText]);

  const commit = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setValue(requirement.restatedText || "");
      return;
    }
    if (trimmed !== requirement.restatedText) {
      onUpdate(requirement.id, {
        restatedText: trimmed,
        category: requirement.category,
      });
    }
  };

  // Keep a ref to the latest commit closure so the unmount-only effect below can
  // call it without depending on value/requirement/onUpdate (which would re-fire
  // on every keystroke, not just on true unmount).
  const commitRef = useRef(commit);
  commitRef.current = commit;
  const isRemovedRef = useRef(isRemoved);
  isRemovedRef.current = isRemoved;

  // Backstop for isVirtualized: a focused row that scrolls out of the viewport
  // unmounts, and React does not reliably fire onBlur on unmount — so an
  // in-progress edit could be silently dropped. commit() already no-ops when the
  // value is blank or unchanged, so this only dispatches for a genuinely dirty edit.
  //
  // Deletion also unmounts the row, and there the backstop must stay quiet: clicking the trash
  // icon dispatches removeRequirementLocal, the row is filtered out, and committing then PUTs
  // /requirements/{id} against a requirement that no longer exists — a 404 through showErrorModal
  // immediately after a successful delete.
  useEffect(() => {
    return () => {
      if (!isRemovedRef.current()) {
        commitRef.current();
      }
    };
  }, []);

  return (
    <textarea
      aria-label={`Requirement text for ${requirement.refLabel}`}
      className={styles.restatedTextInput}
      onBlur={commit}
      onChange={(e) => setValue(e.target.value)}
      rows={2}
      value={value}
    />
  );
};

RestatedTextCell.propTypes = {
  requirement: requirementPropType.isRequired,
  onUpdate: func.isRequired,
  isRemoved: func,
};

// Same commit contract as RestatedTextCell, mirrored for category: always threads
// the row's current restatedText through so a category-only edit doesn't trip the
// server's @NotBlank restatedText validation.
const CategoryCell = ({ requirement, onUpdate }) => {
  const [value, setValue] = useState(requirement.category || "");

  useEffect(() => {
    setValue(requirement.category || "");
  }, [requirement.category]);

  const commit = () => {
    const trimmed = value.trim();
    if (trimmed !== (requirement.category || "")) {
      onUpdate(requirement.id, {
        restatedText: requirement.restatedText,
        category: trimmed || null,
      });
    }
  };

  // A plain input (not the library Input) mirrors RestatedTextCell: the library
  // Input's wrapper has an intrinsic min-width that overflows the fixed-width
  // table cell, spilling into the next column. width:100% + box-sizing keeps it
  // inside the cell.
  return (
    <input
      aria-label={`Category for ${requirement.refLabel}`}
      className={styles.categoryInput}
      onBlur={commit}
      onChange={(e) => setValue(e.target.value)}
      value={value}
    />
  );
};

CategoryCell.propTypes = {
  requirement: requirementPropType.isRequired,
  onUpdate: func.isRequired,
};

const SourceCell = ({ label }) => (
  <span className={styles.sourceLabel} title={label}>
    {label}
  </span>
);

SourceCell.propTypes = {
  label: string.isRequired,
};

// Verification badge plus a quote-popover trigger. sectionRef/pageNumber render as
// plain informational text inside the popover — they are NOT mechanically verified
// provenance, so they must never carry a "verified" affordance of their own.
const VerificationCell = ({ requirement }) => (
  <div className={styles.verificationCell}>
    <VerificationBadge verification={requirement.verification} />
    <TooltipTrigger
      content={
        <div className={styles.quotePopover}>
          <p className={styles.quoteText}>
            {requirement.verbatimQuote
              ? `“${requirement.verbatimQuote}”`
              : "No source quote captured."}
          </p>
          {requirement.sectionRef && (
            <p className={styles.quoteMeta}>Section {requirement.sectionRef}</p>
          )}
          {requirement.pageNumber != null && (
            <p className={styles.quoteMeta}>Page {requirement.pageNumber}</p>
          )}
        </div>
      }
      maxWidth="320px"
    >
      <NewButton
        aria-label={`View source quote for ${requirement.refLabel}`}
        leftIcon={<Quote size={14} />}
        size={NewButtonSize.sm}
        variant={NewButtonVariant.l3}
      />
    </TooltipTrigger>
  </div>
);

VerificationCell.propTypes = {
  requirement: requirementPropType.isRequired,
};

const RequirementActionsCell = ({ requirement, onRemove }) => (
  <NewButton
    aria-label={`Remove requirement ${requirement.refLabel}`}
    leftIcon={<Trash2 size={16} />}
    onClick={() => onRemove(requirement.id)}
    size={NewButtonSize.sm}
    variant={NewButtonVariant.l3}
  />
);

RequirementActionsCell.propTypes = {
  requirement: requirementPropType.isRequired,
  onRemove: func.isRequired,
};

export const buildRequirementColumns = ({
  documentLabelsById,
  onUpdate,
  onRemove,
}) => {
  // Rows unmount for two very different reasons — virtualization scrolling them out, where an
  // in-progress edit must still be committed, and deletion, where committing would PUT against a
  // row that is already gone. Only the delete path can tell them apart, so it records the id here
  // for RestatedTextCell's unmount backstop to check.
  const removedIds = new Set();
  const handleRemove = (id) => {
    removedIds.add(id);
    return onRemove(id);
  };

  return [
    {
      header: "Ref",
      accessorKey: "refLabel",
      size: 80,
    },
    {
      header: "Requirement",
      accessorKey: "restatedText",
      size: 340,
      cell: ({ row }) => (
        <RestatedTextCell
          requirement={row.original}
          onUpdate={onUpdate}
          isRemoved={() => removedIds.has(row.original.id)}
        />
      ),
    },
    {
      header: "Category",
      accessorKey: "category",
      size: 150,
      cell: ({ row }) => (
        <CategoryCell requirement={row.original} onUpdate={onUpdate} />
      ),
    },
    {
      header: "Source Document",
      id: "sourceDocument",
      size: 180,
      cell: ({ row }) => (
        <SourceCell
          label={
            documentLabelsById[row.original.sourceDocumentId] ??
            "Unknown Document"
          }
        />
      ),
    },
    {
      header: "Verification",
      id: "verification",
      size: 160,
      cell: ({ row }) => <VerificationCell requirement={row.original} />,
    },
    {
      header: "",
      id: "actions",
      size: 56,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => (
        <RequirementActionsCell
          requirement={row.original}
          onRemove={handleRemove}
        />
      ),
    },
  ];
};
